import { Big, type RoundingMode } from 'big.js';
import { formatUnits, parseUnits } from 'viem';
import type { Amount } from '@/api/spec';
import { displayLocale } from './displayLocale';

export type NumberFormatOptions = Intl.NumberFormatOptions & {
  displayNonZeroAsMinimal?: boolean;
  trailingZeroDisplay?: 'auto' | 'stripIfInteger';
  decimals?: number;
  minDecimals?: number;
  maxDecimals?: number;
};

export type NumberSeparators = Readonly<{ group: string; decimal: string }>;

const EN_US_SEPARATORS: NumberSeparators = { group: ',', decimal: '.' };
const numberSeparatorsByLocale = new Map<string, NumberSeparators>();

/**
 * Resolves a locale's group/decimal separators once (cached). Locales whose default digits are
 * not Latin (e.g. ar, fa) fall back to en-US separators — mixing ASCII digits with their native
 * separators would produce a hybrid neither audience expects.
 */
export function getNumberSeparators(locale: string): NumberSeparators {
  const cached = numberSeparatorsByLocale.get(locale);
  if (cached) return cached;

  const intlFormat = new Intl.NumberFormat(locale);
  let separators = EN_US_SEPARATORS;
  if (intlFormat.resolvedOptions().numberingSystem === 'latn') {
    const parts = intlFormat.formatToParts(12345.6);
    separators = {
      group: parts.find((part) => part.type === 'group')?.value ?? ',',
      decimal: parts.find((part) => part.type === 'decimal')?.value ?? '.',
    };
  }
  numberSeparatorsByLocale.set(locale, separators);
  return separators;
}

/**
 * Low-level core shared by {@link formatBigEnUS} and {@link formatBigDisplay}: Big-based
 * rounding/trailing-zero/grouping with explicit separators. Exported for tests; call sites
 * should use the two wrappers.
 */
export function formatBig(value: Big, options: Partial<NumberFormatOptions>, separators: NumberSeparators): string {
  let {
    minimumFractionDigits,
    maximumFractionDigits,
    useGrouping = true,
    trailingZeroDisplay = 'auto',
    decimals,
    minDecimals,
    maxDecimals,
  } = options;

  if (decimals != null) {
    minimumFractionDigits = decimals;
    maximumFractionDigits = decimals;
  } else {
    if (minDecimals != null) {
      minimumFractionDigits = minDecimals;
    }
    if (maxDecimals != null) {
      maximumFractionDigits = maxDecimals;
    }

    // Smart Defaults
    if (minimumFractionDigits == null) {
      if (maximumFractionDigits != null) {
        // If max is set but min is not, default min to 0
        minimumFractionDigits = 0;
      } else {
        // If neither is set, default both to 2 (legacy behavior)
        minimumFractionDigits = 2;
        maximumFractionDigits = 2;
      }
    }
    // If maximum is not set, it is handled by the generic logic below or defaults to min
  }

  let fracDigits =
    typeof maximumFractionDigits === 'number'
      ? maximumFractionDigits
      : Math.max(value.c?.length && value.e < 0 ? -value.e : 0, minimumFractionDigits ?? 0); // Use 0 if minimumFractionDigits is still null/undefined

  // do not cut below minimumFractionDigits, even if number is integer
  fracDigits = Math.max(fracDigits, minimumFractionDigits ?? 0); // Use 0 if minimumFractionDigits is still null/undefined

  // Integer and fraction digits are kept apart until the final join: some locales group with
  // '.' (de, es, …), so any find-and-replace on a joined string would corrupt the output.
  const fixed = value.toFixed(fracDigits);
  const negative = fixed[0] === '-';
  const unsigned = negative ? fixed.slice(1) : fixed;
  const dotIndex = unsigned.indexOf('.');
  let intPart = dotIndex === -1 ? unsigned : unsigned.slice(0, dotIndex);
  let decPart = dotIndex === -1 ? '' : unsigned.slice(dotIndex + 1);

  if (trailingZeroDisplay === 'stripIfInteger') {
    decPart = decPart.replace(/0+$/, '');
  } else if (trailingZeroDisplay === 'auto') {
    const min = minimumFractionDigits ?? 0;
    decPart = decPart === '' ? '0'.repeat(min) : decPart.replace(/0+$/, '').padEnd(min, '0');
  }

  // Plain loop instead of a lookahead regex: this runs per order-book row on every WS update,
  // and the regex's backtrack-stack reservation tripped Firefox's "too much recursion".
  if (useGrouping && intPart.length > 3) {
    const firstGroupLength = intPart.length % 3 || 3;
    let grouped = intPart.slice(0, firstGroupLength);
    for (let i = firstGroupLength; i < intPart.length; i += 3) {
      grouped += `${separators.group}${intPart.slice(i, i + 3)}`;
    }
    intPart = grouped;
  }

  return (negative ? '-' : '') + intPart + (decPart ? separators.decimal + decPart : '');
}

/** Canonical dot-decimal formatter for values that are subsequently parsed again. */
export function formatBigEnUS(value: Big, options: Partial<NumberFormatOptions> = {}): string {
  return formatBig(value, options, EN_US_SEPARATORS);
}

// Resolved once at module init — the display locale is fixed for the session. The expensive
// operation is constructing an Intl.NumberFormat (never done per call anywhere); this constant
// just spares even the cache lookup. Invalid navigator.language values fall back to en-US.
const DISPLAY_SEPARATORS: NumberSeparators = (() => {
  try {
    return getNumberSeparators(displayLocale);
  } catch {
    return EN_US_SEPARATORS;
  }
})();

/** Browser-locale formatter for read-only display (see {@link displayLocale}). */
export function formatBigDisplay(value: Big, options: Partial<NumberFormatOptions> = {}): string {
  return formatBig(value, options, DISPLAY_SEPARATORS);
}

// Big (BigNumber) converters
export function bigIntToBig(value: bigint, decimals: number): Big {
  const str = formatUnits(value, decimals);
  return new Big(str);
}

export function bigToBigInt(value: Big, decimals: number): bigint {
  return parseUnits(value.round(decimals, 0).toFixed(decimals), decimals);
}

export function amountToBig(value: Amount, decimals: number): Big {
  return new Big(formatUnits(BigInt(value), decimals));
}

export function bigToDecimalString(value: Big): string {
  return value.toString();
}

export function stringToBig(value: string | number): Big {
  try {
    return new Big(value);
  } catch (error) {
    try {
      const globalSentry = (globalThis as any).Sentry;
      if (globalSentry && typeof globalSentry.captureException === 'function') {
        globalSentry.captureException(error, {
          level: 'error',
          tags: { package: 'ui-kit', module: 'bigUtils', function: 'stringToBig' },
          extra: { value },
        });
      }
    } catch (reportingError) {
      console.error(reportingError);
    }

    return new Big(0);
  }
}

export function bigToString(value: Big, precision?: number, roundingMode?: RoundingMode): string {
  if (precision != null) {
    const fixed = value.toFixed(precision, roundingMode);
    // Only strip trailing decimal zeros (not integer trailing zeros)
    if (precision === 0) return fixed;
    return fixed.replace(/\.?0+$/, '');
  }
  return value.toString();
}

// Safe arithmetic operations
export function addBig(...values: (string | number | Big)[]): Big {
  return values.reduce<Big>((sum, val) => {
    const bigVal = val instanceof Big ? val : stringToBig(val.toString());
    return sum.plus(bigVal);
  }, new Big(0));
}

export function multiplyBig(...values: (string | number | Big)[]): Big {
  return values.reduce<Big>((product, val) => {
    const bigVal = val instanceof Big ? val : stringToBig(val.toString());
    return product.times(bigVal);
  }, new Big(1));
}

export function divideBig(numerator: string | number | Big, denominator: string | number | Big): Big {
  const numBig = numerator instanceof Big ? numerator : stringToBig(numerator.toString());
  const denBig = denominator instanceof Big ? denominator : stringToBig(denominator.toString());

  if (denBig.eq(0)) {
    return new Big(0);
  }
  return numBig.div(denBig);
}

export function subtractBig(minuend: string | number | Big, subtrahend: string | number | Big): Big {
  const minBig = minuend instanceof Big ? minuend : stringToBig(minuend.toString());
  const subBig = subtrahend instanceof Big ? subtrahend : stringToBig(subtrahend.toString());
  return minBig.minus(subBig);
}

/**
 * Formats a number, string, or Big instance as an en-US locale string.
 * Defaults to 2 decimal places with smart trailing zero handling.
 */
export function formatNumber(value: number | string | Big | undefined, options: Partial<NumberFormatOptions> = {}): string {
  if (value == null) return '';
  const bigVal = value instanceof Big ? value : stringToBig(value as string | number);
  return formatBigEnUS(bigVal, options);
}

/** Browser-locale counterpart to formatNumber for read-only display. */
export function formatNumberDisplay(
  value: number | string | Big | undefined,
  options: Partial<NumberFormatOptions> = {}
): string {
  if (value == null) return '';
  const bigVal = value instanceof Big ? value : stringToBig(value as string | number);
  return formatBigDisplay(bigVal, options);
}

/**
 * Formats a USD amount with B/M/k abbreviation.
 * - >= 1,000,000,000 → "$2.5B" (max `maxFractionDigits` decimals, trailing zeros stripped)
 * - >= 1,000,000 → "$1.5M" (same decimal handling)
 * - >= 1,000 → "$50k" (no decimals)
 * - smaller → "$500" (no decimals)
 * Negative values are prefixed with `-`: "-$1.5M".
 */
export function formatCompactUsd(
  amount: number | string | Big | undefined,
  { maxFractionDigits = 1 }: { maxFractionDigits?: number } = {}
): string {
  if (amount == null) return '';
  const big = amount instanceof Big ? amount : stringToBig(amount as string | number);
  const sign = big.lt(0) ? '-' : '';
  const abs = big.abs();
  const scaled = (divisor: number) =>
    formatNumberDisplay(abs.div(divisor), {
      minDecimals: 0,
      maxDecimals: maxFractionDigits,
      trailingZeroDisplay: 'stripIfInteger',
    });
  // Rollover comparisons must use the same display locale as the formatted output.
  const thousand = formatNumberDisplay(1000, { minDecimals: 0, maxDecimals: 0 });
  if (abs.gte(1_000_000_000)) {
    return `${sign}$${scaled(1_000_000_000)}B`;
  }
  if (abs.gte(1_000_000)) {
    const mStr = scaled(1_000_000);
    // Rounded millions roll over to a billion — promote to B to avoid e.g. "$1,000M".
    return mStr === thousand ? `${sign}$${scaled(1_000_000_000)}B` : `${sign}$${mStr}M`;
  }
  if (abs.gte(1_000)) {
    const kStr = formatNumberDisplay(abs.div(1_000), { minDecimals: 0, maxDecimals: 0 });
    // Rounded thousands roll over to a million — promote to M to avoid e.g. "$1,000k".
    return kStr === thousand ? `${sign}$${scaled(1_000_000)}M` : `${sign}$${kStr}k`;
  }
  return `${sign}$${formatNumberDisplay(abs, { minDecimals: 0, maxDecimals: 0 })}`;
}
